/**
 * Formats and enriches raw accounting error messages from external platforms (Xero, QBO, MYOB)
 * by replacing raw codes (e.g. 'CAN029', '200') with their human-readable dropdown option names.
 *
 * @param {string|Object} rawMessage - Raw error message or object
 * @param {Object} options
 * @param {Array} [options.accountsList=[]] - List of platform accounts [{ code, name, ... }]
 * @param {Array} [options.taxRatesList=[]] - List of platform tax rates [{ code, name, id, rate, ... }]
 * @returns {string} Human-readable enriched error message
 */
export function formatAccountingErrorMessage(rawMessage, { accountsList = [], taxRatesList = [] } = {}) {
    if (!rawMessage) return 'Sync failed';

    let msg = typeof rawMessage === 'object'
        ? (rawMessage.message || rawMessage.error || JSON.stringify(rawMessage))
        : String(rawMessage);

    if (!msg) return 'Sync failed';

    // 1. Enrich TaxType codes: match e.g. "TaxType code 'XYZ'" or "TaxType 'XYZ'"
    msg = msg.replace(/(TaxType(?:\s+code)?\s+['"])([^'"]+)(['"])/gi, (match, prefix, code) => {
        const found = (taxRatesList || []).find(t =>
            String(t?.code || t?.id || '').trim().toLowerCase() === String(code).trim().toLowerCase() ||
            String(t?.name || '').trim().toLowerCase() === String(code).trim().toLowerCase()
        );
        if (found) {
            const taxDisplayName = found?.name || found?.label || code;
            return `Tax Type '${taxDisplayName}' (${code})`;
        }
        return match;
    });

    // 2. Enrich Account codes: match e.g. "account(?:\s+code)?\s+['"]XYZ['"]"
    msg = msg.replace(/((?:account|Account)(?:\s+code)?\s+['"])([^'"]+)(['"])/gi, (match, prefix, code) => {
        const found = (accountsList || []).find(a =>
            String(a?.code || a?.id || '').trim().toLowerCase() === String(code).trim().toLowerCase()
        );
        if (found) {
            const accountDisplayName = `${found?.code || ''} - ${found?.name || ''}`;
            return `Account '${accountDisplayName}'`;
        }
        return match;
    });

    return msg;
}
